import axios from 'axios';
import { api } from './api';

const getStudyGroupData = async (sessionId: string, studyGroupId: string) => {
  const Cookie = `sessionid=${sessionId}`;
  const result = await axios.get<StudyGroupData>(
    `https://lab.stemi.education/api/v1/records/study_groups/${studyGroupId}/?format=json`,
    {
      headers: {
        Cookie,
        'Content-Type': 'application/json',
      },
    },
  );
  return result.data;
};

export const getStudentsProjects = async (
  students: string[],
  authToken: string,
) => {
  return await api<StudentData>(
    `/projects/student-projects?studentPlatformIds=${students.join(',')}`,
    {},
    authToken,
  );
};

export const getGroupTemplates = async (authToken: string) => {
  return await api<GroupTemplate[]>(
    '/templates?filter=is_group',
    {
      method: 'GET',
    },
    authToken,
  );
};

export const login = async (token: string) => {
  return await api<User>('/auth/login-token', {
    body: { token },
    method: 'POST',
  });
};

export const getInitialData = async (
  sessionId: string,
  token: string,
  studyGroupId: string,
) => {
  const user = await login(token);
  const studyGroupData = await getStudyGroupData(sessionId, studyGroupId);
  const studentData = await getStudentsProjects(
    [...studyGroupData.students.map((s) => s.pk), user.platformId, user.id] ||
      [],
    user.token,
  );
  const groupTemplates = await getGroupTemplates(user.token);
  const missingStudents = (studyGroupData.students || []).filter((student) => {
    return !studentData.students.find(
      (chatbotStudent) => chatbotStudent.platformId === student.pk,
    );
  });

  studentData.students = [
    ...studentData.students,
    ...missingStudents.map((student) => ({
      id: null,
      platformId: student.pk,
      username: student.email,
    })),
  ];

  return { studentData, studyGroupData, user, groupTemplates };
};

export interface School {
  pk: string;
  url: string;
  name: string;
}

export interface Thumbnails {
  '754x425': string;
  '75x75': string;
}

export interface Program {
  pk: string;
  url: string;
  name: string;
  thumbnails: Thumbnails;
  video?: any;
  description: string;
  progress: number;
}

export interface Period {
  pk: string;
  name: string;
  first_date: string;
  last_date: string;
}

export interface License {
  pk: string;
  url: string;
  school: School;
  program: Program;
  period: Period;
}

export interface Student {
  pk: string;
  url: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  pending: boolean;
}

export interface Teacher {
  pk: string;
  url: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  pending: boolean;
}

export interface StudyGroupData {
  pk: string;
  url: string;
  name: string;
  teachers_names: string;
  license: License;
  students: Student[];
  teachers: Teacher[];
}

export interface StudentData {
  students: ChatbotStudent[];
  studentProjects: Record<string, StudentProject[]>;
  sections: Section[];
}

export interface StudentProject {
  id: string;
  name: string;
  type: string;
  sectionId: string;
  sectionName: string;
  createdAt: string;
  updatedAt: string;
  templateName: string;
  templateId: string;
  isGroupProject: boolean;
  isShown: boolean;
  attributes: Record<string, any>;
  collaborators?: ChatbotStudent[];
  answersId?: string;
}

export interface Template {
  id: string;
  name: string;
  index: number;
  slug: string;
}

export interface Section {
  id: string;
  index: number;
  name: string;
  templates: Template[];
  namespace?: string;
  public?: boolean;
}

export interface ChatbotStudent {
  id: string | null;
  platformId: string;
  username: string;
}

export interface User {
  id: string;
  platformId: string;
  role: string;
  token: string;
  username: string;
}

export interface GroupTemplate {
  id: string;
  name: string;
  slug: string;
  type: string;
  namespace: string;
}
